import { z } from "zod";
export const ENVIRONMENT_SCHEMA_VERSION = 1;
export const ENVIRONMENT_SCHEMA_VALIDATION_REASON_CODE = "invalid-environment-record";
export const environmentIdSchema = z.string().min(1).max(160);
export const environmentDigestSchema = z
    .string()
    .regex(/^sha256:[a-f0-9]{64}$/);
export const environmentIsoTimestampSchema = z
    .string()
    .datetime({ offset: true });
export const environmentNonNegativeIntegerSchema = z
    .number()
    .int()
    .nonnegative();
export const environmentPositiveIntegerSchema = z.number().int().positive();
export const ENVIRONMENT_KIND_V1 = [
    "device",
    "server",
    "cluster",
    "service",
];
export const ENVIRONMENT_TOPOLOGY_PROFILE_V1 = [
    "host",
    "fleet",
    "ci-pool",
    "sandbox-pool",
    "cluster",
    "replicated-service",
    "service",
];
export const ENVIRONMENT_STAGE_LABEL_V1 = [
    "development",
    "sandbox",
    "staging",
    "production",
];
export const ENVIRONMENT_DATA_AUTHORITY_V1 = [
    "resident",
    "delegated",
    "offline",
];
export const ENVIRONMENT_AUTOMATION_PROFILE_V1 = [
    "manual",
    "one-click",
    "policy-managed",
];
export const ENVIRONMENT_LIFECYCLE_STATE_V1 = [
    "reserved",
    "enrolling",
    "active",
    "retiring",
    "quarantined",
    "revoked",
    "removed",
];
export const ENVIRONMENT_TRUST_STATE_V1 = [
    "unverified",
    "pinned",
    "attested",
    "rotation_due",
    "trust_failed",
    "revoked",
];
export const ENVIRONMENT_CONNECTIVITY_V1 = [
    "online",
    "degraded",
    "offline",
    "unknown",
];
export const ENVIRONMENT_FRESHNESS_V1 = [
    "fresh",
    "aging",
    "stale",
    "never_observed",
];
export const ENROLLMENT_IDENTITY_KEY_ROLE_V1 = [
    "environment-root",
    "environment-generation",
    "environment-node",
    "execution-occurrence",
];
export const ENVIRONMENT_ACTION_TYPE_REGISTRY_V1 = [
    { actionTypeId: "environment.health.read", consequenceClass: "read-only" },
    {
        actionTypeId: "environment.capabilities.read",
        consequenceClass: "read-only",
    },
    {
        actionTypeId: "environment.agent.install",
        consequenceClass: "reversible-mutation",
    },
    {
        actionTypeId: "environment.agent.upgrade",
        consequenceClass: "reversible-mutation",
    },
    {
        actionTypeId: "environment.service.restart",
        consequenceClass: "reversible-mutation",
    },
    {
        actionTypeId: "environment.reviewed-command.execute",
        consequenceClass: "reviewed-mutation",
    },
];
export const ENVIRONMENT_OPERATION_PRODUCT_CONTROLS_V1 = {
    status: "environment.operation.status",
    watch: "environment.operation.watch",
    cancel: "environment.operation.cancel",
    resume: "environment.operation.resume",
    reconcile: "environment.operation.reconcile",
};
export const environmentActionTypeIdSchema = z.enum(ENVIRONMENT_ACTION_TYPE_REGISTRY_V1.map((entry) => entry.actionTypeId));
export const publicKeyDescriptorSchema = z
    .object({
    algorithm: z.string().min(1),
    keyId: z.string().min(1),
    encodedKeyBytes: z.string().min(1),
})
    .strict();
export const enrollmentIdentityKeyRoleSchema = z.enum(ENROLLMENT_IDENTITY_KEY_ROLE_V1);
export const canonicalEnrollmentPublicKeyFingerprintSchema = environmentDigestSchema;
export const roleBoundEnrollmentPublicKeySchema = publicKeyDescriptorSchema
    .extend({
    keyRole: enrollmentIdentityKeyRoleSchema,
    publicKeyFingerprint: canonicalEnrollmentPublicKeyFingerprintSchema,
})
    .strict();
export const environmentTenantAuthoritySchema = z.discriminatedUnion("authorityState", [
    z
        .object({
        authorityState: z.literal("local-origin"),
        originTenantId: environmentIdSchema,
        authorityTenant: z.literal("same-as-origin"),
        authorityBindingDigest: environmentDigestSchema,
        authorityClaimCertificateDigest: z.null(),
    })
        .strict(),
    z
        .object({
        authorityState: z.literal("claimed-account"),
        originTenantId: environmentIdSchema,
        authorityTenantId: environmentIdSchema,
        authorityBindingDigest: environmentDigestSchema,
        authorityClaimCertificateDigest: environmentDigestSchema,
    })
        .strict(),
]);
const environmentOriginIdentityShape = {
    originTenantId: environmentIdSchema,
    environmentId: environmentIdSchema,
    generationId: environmentIdSchema,
    environmentRootPublicKey: publicKeyDescriptorSchema,
    displayName: z.string().min(1),
    kind: z.enum(ENVIRONMENT_KIND_V1),
    topologyProfile: z.enum(ENVIRONMENT_TOPOLOGY_PROFILE_V1),
    createdAt: environmentIsoTimestampSchema,
};
export const environmentOriginIdentitySchema = z
    .object(environmentOriginIdentityShape)
    .strict();
export const resolvedEnvironmentIdentitySchema = z
    .object({
    ...environmentOriginIdentityShape,
    resolvedAuthority: environmentTenantAuthoritySchema,
})
    .strict()
    .superRefine((identity, context) => {
    if (identity.resolvedAuthority.originTenantId !== identity.originTenantId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["resolvedAuthority", "originTenantId"],
            message: "resolved authority origin must match the immutable origin identity",
        });
    }
});
export const resolvedEnvironmentScopeMemberSchema = z
    .object({
    environmentId: environmentIdSchema,
    generationId: environmentIdSchema,
    admittedResourceScopeDigest: environmentDigestSchema,
})
    .strict();
export const environmentQueryScopeSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("environment"),
        environmentId: environmentIdSchema,
        generationId: environmentIdSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("environment-set"),
        environmentIds: z.array(environmentIdSchema).min(1),
        environmentSetDigest: environmentDigestSchema,
    })
        .strict(),
]);
export const environmentVerificationScopeSchema = z
    .object({
    kind: z.literal("resolved-environment-scope"),
    members: z.array(resolvedEnvironmentScopeMemberSchema).min(1),
    environmentScopeDigest: environmentDigestSchema,
})
    .strict();
export const exactEnvironmentSubjectScopeSchema = z
    .object({
    tenantId: environmentIdSchema,
    environmentId: environmentIdSchema,
    generationId: environmentIdSchema,
    authorityBindingDigest: environmentDigestSchema,
})
    .strict();
export const environmentCapabilitiesSchema = z
    .object({
    observePresence: z.boolean(),
    observeMetadata: z.boolean(),
    observeSemantics: z.boolean(),
    liveEvents: z.boolean(),
    collectEvidence: z.boolean(),
    runDeterministicChecks: z.boolean(),
    runSemanticChecks: z.boolean(),
    files: z.enum(["none", "metadata", "read", "scoped-write"]),
    shell: z.enum(["none", "read-only", "approved-command", "interactive"]),
    browser: z.enum(["none", "observe", "approved-action", "interactive"]),
    externalActions: z.array(z.string().min(1)),
    offlineReplay: z.boolean(),
})
    .strict();
export const environmentHealthSchema = z
    .object({
    connectivity: z.enum(ENVIRONMENT_CONNECTIVITY_V1),
    freshness: z.enum(ENVIRONMENT_FRESHNESS_V1),
    trust: z.enum(ENVIRONMENT_TRUST_STATE_V1),
    requiredEndpointSetDigest: environmentDigestSchema,
    observedEndpointSetDigest: environmentDigestSchema,
    endpointHealthRowSetDigest: environmentDigestSchema,
    requiredEndpointCount: environmentNonNegativeIntegerSchema,
    onlineEndpointCount: environmentNonNegativeIntegerSchema,
    degradedEndpointCount: environmentNonNegativeIntegerSchema,
    offlineEndpointCount: environmentNonNegativeIntegerSchema,
    unknownEndpointCount: environmentNonNegativeIntegerSchema,
    exactEndpointCountEquationDigest: environmentDigestSchema,
    lastContactAt: environmentIsoTimestampSchema.nullable(),
    lastProjectionAt: environmentIsoTimestampSchema.nullable(),
})
    .strict()
    .superRefine((health, context) => {
    const classifiedCount = health.onlineEndpointCount +
        health.degradedEndpointCount +
        health.offlineEndpointCount +
        health.unknownEndpointCount;
    if (classifiedCount !== health.requiredEndpointCount) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["requiredEndpointCount"],
            message: "endpoint counts must exactly partition the required endpoint set",
        });
    }
});
export const environmentWorkContextBindingSchema = z
    .object({
    id: environmentIdSchema,
    environmentId: environmentIdSchema,
    workContextId: environmentIdSchema,
    locatorKind: z.enum([
        "repository",
        "directory",
        "service",
        "workflow",
        "manual",
    ]),
    locatorDigest: environmentDigestSchema,
    displayLocator: z.string().min(1),
    observedAt: environmentIsoTimestampSchema,
    confidence: z.number().min(0).max(1),
    status: z.enum(["observed", "linked", "missing", "archived"]),
})
    .strict();
export const environmentProviderRefSchema = z
    .object({
    provider: z.string().min(1),
    externalId: z.string().min(1),
})
    .strict();
const environmentBaseShape = {
    originTenantId: environmentIdSchema,
    id: environmentIdSchema,
    slug: z.string().min(1).max(160),
    displayName: z.string().min(1),
    kind: z.enum(ENVIRONMENT_KIND_V1),
    topologyProfile: z.enum(ENVIRONMENT_TOPOLOGY_PROFILE_V1),
    stageLabel: z.enum(ENVIRONMENT_STAGE_LABEL_V1).nullable(),
    providerRef: environmentProviderRefSchema.nullable(),
    generationId: environmentIdSchema,
    dataAuthority: z.enum(ENVIRONMENT_DATA_AUTHORITY_V1),
    rawEvidenceLocation: z.string().min(1),
    effectivePolicyDigest: environmentDigestSchema,
    capabilitiesDigest: environmentDigestSchema,
    enrollmentPolicyRef: z.string().min(1).nullable(),
    installationStateDigest: environmentDigestSchema.nullable(),
    automationProfile: z.enum(ENVIRONMENT_AUTOMATION_PROFILE_V1),
    lastContactAt: environmentIsoTimestampSchema.nullable(),
    lastProjectionAt: environmentIsoTimestampSchema.nullable(),
    createdAt: environmentIsoTimestampSchema,
};
export const environmentBaseSchema = z.object(environmentBaseShape).strict();
const noActivationLinkageShape = {
    enrollmentReceiptDigest: z.null(),
    activationReceiptDigest: z.null(),
    firstProjectionDigest: z.null(),
};
const enrolledOnlyActivationLinkageShape = {
    enrollmentReceiptDigest: environmentDigestSchema,
    activationReceiptDigest: z.null(),
    firstProjectionDigest: z.null(),
};
const completeActivationLinkageShape = {
    enrollmentReceiptDigest: environmentDigestSchema,
    activationReceiptDigest: environmentDigestSchema,
    firstProjectionDigest: environmentDigestSchema,
};
export const environmentHistoricalActivationLinkageSchema = z.union([
    z.object(noActivationLinkageShape).strict(),
    z.object(enrolledOnlyActivationLinkageShape).strict(),
    z.object(completeActivationLinkageShape).strict(),
]);
function environmentStateSchema(stateShape) {
    return z.object({ ...environmentBaseShape, ...stateShape }).strict();
}
const historicalEnvironmentStateSchemas = (stateShape) => [
    environmentStateSchema({ ...stateShape, ...noActivationLinkageShape }),
    environmentStateSchema({
        ...stateShape,
        ...enrolledOnlyActivationLinkageShape,
    }),
    environmentStateSchema({
        ...stateShape,
        ...completeActivationLinkageShape,
    }),
];
const quarantinedEnvironmentSchemas = historicalEnvironmentStateSchemas({
    lifecycleState: z.literal("quarantined"),
    trustState: z.enum(["unverified", "trust_failed"]),
    retiredAt: z.null(),
});
const revokedEnvironmentSchemas = historicalEnvironmentStateSchemas({
    lifecycleState: z.literal("revoked"),
    trustState: z.literal("revoked"),
    retiredAt: z.null(),
});
const removedEnvironmentSchemas = historicalEnvironmentStateSchemas({
    lifecycleState: z.literal("removed"),
    trustState: z.enum(ENVIRONMENT_TRUST_STATE_V1),
    retiredAt: environmentIsoTimestampSchema,
});
export const environmentSchema = z.union([
    environmentStateSchema({
        lifecycleState: z.literal("reserved"),
        trustState: z.enum(["unverified", "pinned"]),
        ...noActivationLinkageShape,
        retiredAt: z.null(),
    }),
    environmentStateSchema({
        lifecycleState: z.literal("enrolling"),
        trustState: z.enum(["unverified", "pinned", "attested", "trust_failed"]),
        enrollmentReceiptDigest: environmentDigestSchema.nullable(),
        activationReceiptDigest: z.null(),
        firstProjectionDigest: z.null(),
        retiredAt: z.null(),
    }),
    environmentStateSchema({
        lifecycleState: z.literal("active"),
        trustState: z.enum(["attested", "rotation_due"]),
        ...completeActivationLinkageShape,
        retiredAt: z.null(),
    }),
    environmentStateSchema({
        lifecycleState: z.literal("retiring"),
        trustState: z.enum(["attested", "rotation_due"]),
        ...completeActivationLinkageShape,
        retiredAt: z.null(),
    }),
    ...quarantinedEnvironmentSchemas,
    ...revokedEnvironmentSchemas,
    ...removedEnvironmentSchemas,
]);
export const environmentActionActorSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("authenticated-principal"),
        tenantId: environmentIdSchema,
        principalId: environmentIdSchema,
        authenticationContextDigest: environmentDigestSchema,
        currentTenantAuthorizationDigest: environmentDigestSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("authorized-service"),
        tenantId: environmentIdSchema,
        servicePrincipalId: environmentIdSchema,
        workloadIdentityDigest: environmentDigestSchema,
        currentTenantAuthorizationDigest: environmentDigestSchema,
    })
        .strict(),
]);
export const environmentActionIntentSchema = z
    .object({
    tenantId: environmentIdSchema,
    environmentId: environmentIdSchema,
    environmentGenerationId: environmentIdSchema,
    admittedResourceScopeDigest: environmentDigestSchema,
    actor: environmentActionActorSchema,
    actionTypeId: environmentActionTypeIdSchema,
    canonicalActionParametersDigest: environmentDigestSchema,
})
    .strict()
    .superRefine((intent, context) => {
    if (intent.actor.tenantId !== intent.tenantId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["actor", "tenantId"],
            message: "action actor tenant must match the action tenant",
        });
    }
});
export function lookupEnvironmentActionType(actionTypeId) {
    return (ENVIRONMENT_ACTION_TYPE_REGISTRY_V1.find((entry) => entry.actionTypeId === actionTypeId) ?? null);
}
export function validateEnvironment(value) {
    if (!environmentSchema.safeParse(value).success) {
        return { ok: false, errors: [ENVIRONMENT_SCHEMA_VALIDATION_REASON_CODE] };
    }
    return { ok: true, errors: [] };
}
